import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TreeNode } from 'primeng/api';
import { DisplayedProduct } from 'src/app/pages/tunnel/common/base-complementary-product/base-complementary-product.component';
import { faRedo } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject } from 'rxjs';

export const ALL_PRODUCT_TREE_NODE: TreeNode = {
  key: 'all', // Ajout de la clé unique
  label: 'Tous',
  data: { type: 'all' },
  expanded: false,
  children: [],
};

@Component({
  selector: 'ng-casto-complementary-product-filter',
  templateUrl: './complementary-product-filter.component.html',
})
export class ComplementaryProductFilterComponent implements OnInit {
  @Input() products!: DisplayedProduct[];
  @Input() isOpen!: boolean;
  @Input() selectedFilters$!: BehaviorSubject<TreeNode[]>;
  @Output() isOpenChange = new EventEmitter<boolean>();
  protected categories: TreeNode[] = [];

  protected readonly faRedo = faRedo;

  ngOnInit() {
    this.initCategories();

    this.selectedFilters$.subscribe(filters => {
      if (filters?.length === 0) {
        this.setDefaultSubCategories();
      }
    });
  }

  private initCategories(): void {
    const categoryNodes = new Map<string, TreeNode>();

    for (const product of this.products) {
      const categoryName = product.category?.categoryName;
      if (!categoryName) continue;

      // Récupère ou crée le nœud catégorie
      let categoryNode = categoryNodes.get(categoryName);
      if (!categoryNode) {
        categoryNode = {
          key: `cat-${categoryName}`, // Ajout de la clé unique
          label: categoryName,
          data: { type: 'category', name: categoryName },
          expanded: false,
          children: [],
        };
        categoryNodes.set(categoryName, categoryNode);
      }

      // Ajoute la sous-catégorie si elle n'existe pas déjà
      const subCategoryName = product.subCategory?.subCategoryName;
      if (subCategoryName && !categoryNode.children?.some(child => child.label === subCategoryName)) {
        categoryNode.children!.push({
          key: `subcat-${categoryName}-${subCategoryName}`, // Ajout de la clé unique
          label: subCategoryName,
          data: { type: 'subcategory', name: subCategoryName, parentKey: categoryNode.key },
        });
      }
    }

    // Tri et construction finale
    this.categories = [
      ALL_PRODUCT_TREE_NODE,
      ...Array.from(categoryNodes.values())
        .sort((a, b) => a.label!.localeCompare(b.label!, 'fr'))
        .map(node => ({
          ...node,
          children: node.children?.sort((a, b) => a.label!.localeCompare(b.label!, 'fr')),
        })),
    ];
  }

  protected setDefaultSubCategories() {
    this.selectedFilters$.next([this.categories.find(c => c.key === 'all')]);
  }

  get selectedFilters(): TreeNode[] {
    return this.selectedFilters$.value;
  }

  set selectedFilters(value: TreeNode[]) {
    this.selectedFilters$.next(value);
  }

  protected getNodeChildrenEnabledCount(node: TreeNode): number {
    return this.selectedFilters.filter(f => node.children.some(c => c.key === f.key)).length;
  }

  protected onNodeSelect(node: TreeNode) {
    // Si on sélectionne 'all', on vide tout
    if (node.key === 'all') {
      this.selectedFilters$.next([node]);
      return;
    }
    // Retirer 'all' s'il était sélectionné
    let newFilters = this.selectedFilters$.value.filter(n => n.key !== 'all');

    this.selectedFilters$.next(newFilters);
  }

  private getParentKey(node: TreeNode): string | null {
    // Si le nœud a un parent direct
    if (node.parent) {
      return node.parent.key as string;
    }

    // Sinon, chercher dans l'arbre des catégories
    for (const category of this.categories) {
      if (category.children?.some(child => child.key === node.key)) {
        return category.key as string;
      }
    }

    // Si c'est une catégorie racine, retourner sa propre clé
    return node.key as string;
  }
}
